<div class="individual-details-list-component">

  @if (autosaveFailed) {
  <div class="row separator-bottom">
    <div class="col-12">
      <message-box type="warn">
        <p>Your latest changes could not be auto-saved. They are kept on this page and will be retried automatically.</p>
      </message-box>
    </div>
  </div>
  }

  @if (documentsLoadFailed) {
  <div class="row separator-bottom">
    <div class="col-12">
      <message-box type="warn">
        <p>We could not check the uploaded identification documents. Retry before continuing.</p>
        <button mat-button type="button" (click)="reload()">Retry</button>
      </message-box>
    </div>
  </div>
  }

  <!-- The list is re-read from the backend every time the step is entered, and the individuals it
       shows are created by the KYB and guarantor steps, so an empty table during that read reads as
       "there is nobody" rather than "not loaded yet". -->
  @if (!loaded) {
  <div class="individuals-loading">
    <mat-spinner [diameter]="26" [strokeWidth]="2"></mat-spinner>
    <span>Loading individuals...</span>
  </div>
  } @else {

  @if (missingPropertyOwner()) {
  <div class="row separator-bottom">
    <div class="col-12">
      <message-box type="warn">
        <p>This application was quoted as a property owner, so at least one individual must be marked as a property
          owner.</p>
      </message-box>
    </div>
  </div>
  }

  <div class="row separator-bottom">
    <div class="col-lg-6 col-md-12">
      <mat-form-field>
        <mat-label>Primary contact</mat-label>
        <mat-select [formControl]="formControlPrimaryContact"
          (selectionChange)="onPrimaryContactSelectionChange($event.value)">
          @for (row of rows; track row.individual.id) {
          <mat-option [value]="row.individual.guid">{{row.individual.givenName}} {{row.individual.surname}}</mat-option>
          }
        </mat-select>
        @if (formControlPrimaryContact.touched && formControlPrimaryContact.hasError('required')) {
        <mat-error>Please select a primary contact</mat-error>
        }
      </mat-form-field>
    </div>
  </div>

  <mat-card appearance="outlined">
    <mat-card-content class="px-0">
      <div class="row">
        <div class="col-12 individual-details-section">
          <table mat-table class="individuals-table" [dataSource]="tableData" multiTemplateDataRows>
            <ng-container matColumnDef="dir">
              <th mat-header-cell *matHeaderCellDef class="column-width-0"></th>
              <td mat-cell *matCellDef="let element" class="cell cell-padding">
                @if (isTableRowExpanded(element)) {
                <span class="mdi mdi-chevron-up"></span>
                }
                @if (!isTableRowExpanded(element)) {
                <span class="mdi mdi-chevron-down"></span>
                }
              </td>
            </ng-container>
            <ng-container matColumnDef="name">
              <th mat-header-cell *matHeaderCellDef class="column-width-1">Name</th>
              <td mat-cell *matCellDef="let element" class="cell ellipsis"
                matTooltip="{{displayName(element)}}">{{displayName(element)}}
                @if (element.isPrimaryContact) {
                <tag-box [state]="'Primary Contact'"></tag-box>
                }
              </td>
            </ng-container>
            <ng-container matColumnDef="dob">
              <th mat-header-cell *matHeaderCellDef class="column-width-1">Date of Birth</th>
              <td mat-cell *matCellDef="let element" class="cell">{{displayDob(element)}}</td>
            </ng-container>
            <ng-container matColumnDef="address">
              <th mat-header-cell *matHeaderCellDef>Address</th>
              <td mat-cell *matCellDef="let element" class="cell">
                <span [innerHTML]="displayAddress(element) | addressSplit | sanitizeHtml"></span>
              </td>
            </ng-container>
            <ng-container matColumnDef="businessRole">
              <th mat-header-cell *matHeaderCellDef class="column-width-1">Business Role</th>
              <td mat-cell *matCellDef="let element" class="cell">{{displayBusinessRole(element)}}</td>
            </ng-container>
            <ng-container matColumnDef="applicationRole">
              <th mat-header-cell *matHeaderCellDef class="column-width-1">Application Role</th>
              <td mat-cell *matCellDef="let element" class="cell">{{displaySignerRole(element) | signerRole}}</td>
            </ng-container>
            <ng-container matColumnDef="status">
              <th mat-header-cell *matHeaderCellDef class="column-width-0"></th>
              <td mat-cell *matCellDef="let element" class="cell">
                @if (rowInvalid(element)) {
                <span class="mdi mdi-alert-circle-outline incomplete"
                  matTooltip="Required details are missing for this individual"></span>
                }
                @if (!rowInvalid(element)) {
                <span class="mdi mdi-check-circle-outline ok" matTooltip="All required details provided"></span>
                }
              </td>
            </ng-container>

            <ng-container matColumnDef="expandedDetail">
              <td mat-cell *matCellDef="let element" [attr.colspan]="columnsToDisplay.length">
                <div class="element-detail" [@detailExpand]="isTableRowExpanded(element) ? 'expanded' : 'collapsed'">
                  @if (rowFormControl(element)) {
                  <div>
                    <div class="row">
                      <div class="col-12">
                        <div class="mat-h3 ellipsis individual-name"
                          matTooltip="{{displayName(element)}} - {{displaySignerRole(element) | signerRole}}">
                          {{displayName(element)}} - {{displaySignerRole(element) | signerRole}}</div>
                      </div>
                    </div>
                    <div class="row">
                      <div class="col-12">
                        <individual-form
                          [mark]="markObservable"
                          [showPropertyOwner]="true"
                          [showIdDocumentUpload]="true"
                          [idDocumentProvided]="rowIdDocumentProvided(element)"
                          [existingIdDocuments]="rowIdDocuments(element)"
                          [showResidencyProofAlert]="residencyAlertFor(element)"
                          (idDocumentDelete)="onIdDocumentDelete(element, $event)"
                          [formControl]="rowFormControl(element)!">
                        </individual-form>
                      </div>
                    </div>
                  </div>
                  }
                </div>
              </td>
            </ng-container>

            <tr mat-header-row *matHeaderRowDef="columnsToDisplay"></tr>
            <tr mat-row *matRowDef="let element; columns: columnsToDisplay" class="cursor-pointer"
              [class.expanded-row]="isTableRowExpanded(element)" (click)="expandTableRow(element)"></tr>
            <tr class="detail-row" [ngClass]="{'detail-row-expanded': isTableRowExpanded(element)}"
              [ngStyle]="{'display': isTableRowExpanded(element) ? null : 'none'}" mat-row
              *matRowDef="let element; columns: ['expandedDetail']"></tr>
          </table>
        </div>
      </div>
    </mat-card-content>
  </mat-card>

  }
</div>
